import type {
  CommandCenterResponse,
  CommandCenterOverviewTab,
  CommandCenterResourceItem,
  CommandCenterRisk,
  CommandCenterSummaryMetric,
} from '../services/commandCenterService';
import { formatExecutionPostureLabel } from './executionPosture';
import { normalizePrimaryValueDriverText } from './executiveCopy';

export type InitiativeStatus =
  | 'Approval Pending'
  | 'Approved'
  | 'Activation Required'
  | 'Planning'
  | 'Active'
  | 'At Risk'
  | 'On Hold'
  | 'Complete'
  | 'Value Tracking';

export type MilestoneStatus = 'Not Started' | 'In Progress' | 'At Risk' | 'Complete' | 'Delayed';
export type RiskSeverity = 'Low' | 'Moderate' | 'High';
export type WorkstreamStatus = 'Not Started' | 'In Progress' | 'Complete' | 'Blocked';

export interface MappedMilestone {
  id: string;
  title: string;
  phase: string;
  owner: string;
  dueDate: string;
  status: MilestoneStatus;
  completionPct: number;
  blockers?: string;
  valueNote?: string;
}

export interface MappedRisk {
  id: string;
  title: string;
  description: string;
  severity: RiskSeverity;
  likelihood: 'Low' | 'Medium' | 'High';
  owner: string;
  mitigation: string;
  status: 'Open' | 'Mitigated' | 'Resolved';
}

export interface MappedWorkstream {
  id: string;
  name: string;
  owner: string;
  status: WorkstreamStatus;
  targetDate: string;
  notes: string;
}

export interface MappedTeamMember {
  id?: string;
  role: string;
  name: string;
  title: string;
}

export interface MappedApprovalSignoff {
  completedCount: number;
  currentStepId: string | null;
  allComplete: boolean;
  steps: {
    id: string;
    label: string;
    cfo?: boolean;
    status: 'done' | 'current' | 'pending';
    source?: string | null;
  }[];
}

export interface MappedMetricRow {
  label: string;
  baseline: string;
  current: string;
  target: string;
}

export interface MappedResourceItem {
  id: string;
  label: string;
  desc: string;
  action: CommandCenterResourceItem['action'];
  available: boolean;
  path?: string;
  content?: Record<string, unknown>;
  targetTab?: string;
}

export interface MappedOverviewRisk {
  id: string;
  title: string;
  severity: RiskSeverity;
  severityDisplay: string;
}

export interface MappedOverview {
  nextAction: string | null;
  objective: string | null;
  strategicValueDriver: string | null;
  whyApproved: string | null;
  expectedOutcomes: string[];
  topPriorities: string[];
  topRisks: MappedOverviewRisk[];
  decisionReferences: { id: string; label: string; path?: string }[];
}

export interface MappedSummaryMetric {
  id: string;
  label: string;
  value: string;
  sub: string;
  progressPercent?: number;
}

export interface CommandCenterViewModel {
  useCaseId: string;
  clientId: string;
  header: {
    title: string;
    breadcrumb: string;
    categoryName: string;
    status: InitiativeStatus;
    owner: string;
    sponsor: string;
    timeline: string;
    payback: string;
    roiRange: string;
    /** Implementation cost display (e.g. "$547K"), or "—" when unset */
    investment: string;
    confidence: string;
    duration: string;
    phase: string;
    summaryMetrics: MappedSummaryMetric[];
  };
  overview: MappedOverview;
  activationChecklist: { index?: number; label: string; done: boolean; status: string }[];
  checklistSummary: { total: number; completed: number; progressPercent: number };
  workstreams: MappedWorkstream[];
  milestones: MappedMilestone[];
  milestoneSummary: { total: number; complete: number; inProgress: number; notStarted: number; delayed: number };
  milestoneExportPath?: string;
  risks: MappedRisk[];
  riskSummary: { high: number; moderate: number; resolved: number; open: number };
  team: MappedTeamMember[];
  approvalSignoff: MappedApprovalSignoff;
  metrics: {
    banner: string | null;
    financial: MappedMetricRow[];
    operational: MappedMetricRow[];
    clinical: MappedMetricRow[];
    delivery: MappedMetricRow[];
    roiNarrative: string | null;
    /** Raw implementation cost from financial_kpis when present */
    implementationCost: number | null;
  };
  resources: {
    approvalMaterials: MappedResourceItem[];
    decisionReferences: MappedResourceItem[];
    executionMaterials: MappedResourceItem[];
  };
  sidebar: {
    status: InitiativeStatus;
    owner: string;
    sponsor: string;
    phase: string;
    nextMilestone: {
      title: string;
      dueDate: string;
      completionPct: number;
      progressDisplay: string;
    } | null;
    topRisk: { title: string; description: string; severity: RiskSeverity; severityDisplay: string } | null;
    quickActions: { id: string; label: string; action: string; target?: string; path?: string; resourceId?: string }[];
  };
}

const FALLBACK = '—';

function orFallback(value: string | null | undefined, fallback = FALLBACK): string {
  if (value == null || value === '') return fallback;
  return value;
}

/** Format KPI values like "30000000 USD" → "$30,000,000" for display consistency. */
function formatMetricDisplayValue(raw: string): string {
  if (!raw || raw === FALLBACK || raw === 'Not yet measured') return raw;
  const match = raw.trim().match(/^(-?\d+(?:\.\d+)?)\s*(USD|usd|\$)?$/);
  if (!match) return raw;
  const n = Number(match[1]);
  if (!Number.isFinite(n)) return raw;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: Number.isInteger(n) ? 0 : 2,
  }).format(n);
}

/** True when a display string means “no person assigned yet”. */
function isUnassignedLabel(value: string | null | undefined): boolean {
  if (value == null) return true;
  const trimmed = value.trim();
  if (!trimmed) return true;
  const normalized = trimmed.toLowerCase();
  return (
    normalized === '—'
    || normalized === '-'
    || normalized === 'not assigned'
    || normalized === 'unassigned'
    || normalized === 'n/a'
    || normalized === 'tbd'
  );
}

/** Prefer a real metadata name; otherwise the first Team member with that role. */
function resolvePersonName(
  metadataValue: string | null | undefined,
  team: { role: string; name: string }[],
  roleName: string,
): string {
  if (!isUnassignedLabel(metadataValue)) {
    return String(metadataValue).trim();
  }
  const needle = roleName.trim().toLowerCase();
  const member = team.find((row) => (row.role || '').trim().toLowerCase() === needle);
  const name = member?.name?.trim();
  if (name && !isUnassignedLabel(name)) return name;
  return 'Not assigned';
}

function optionalText(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  return normalizeOverviewListItem(value);
}

function coerceDisplayText(value: unknown, fallback = FALLBACK): string {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || fallback;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return normalizeOverviewListItem(value) ?? fallback;
}

function normalizeOverviewListItem(item: unknown): string | null {
  if (item == null) return null;
  if (typeof item === 'string') {
    const trimmed = item.trim();
    return trimmed || null;
  }
  if (typeof item === 'object') {
    const obj = item as Record<string, unknown>;
    const label = [obj.label, obj.title, obj.name, obj.description]
      .find((value) => typeof value === 'string' && value.trim()) as string | undefined;
    if (!label) return null;

    const parts = [label.trim()];
    if (typeof obj.due_date_display === 'string' && obj.due_date_display.trim()) {
      parts.push(`Due ${obj.due_date_display.trim()}`);
    } else if (typeof obj.due_date === 'string' && obj.due_date.trim()) {
      parts.push(`Due ${obj.due_date.trim()}`);
    }
    if (typeof obj.phase === 'string' && obj.phase.trim()) {
      parts.push(`Phase: ${obj.phase.trim()}`);
    }
    return parts.join(' · ');
  }
  return String(item).trim() || null;
}

function normalizeOverviewList(items: unknown[] | undefined): string[] {
  return (items ?? [])
    .map(normalizeOverviewListItem)
    .filter((item): item is string => Boolean(item));
}

/** Safely format API values that may be plain strings or milestone-like objects. */
export function formatApiDisplayText(value: unknown): string {
  return coerceDisplayText(value, '—');
}

function normalizeTabTarget(target: string | undefined): string | undefined {
  if (!target) return target;
  if (target === 'plans') return 'plan';
  return target;
}

function mapInitiativeStatus(raw: string | null | undefined, progressPercent = 0): InitiativeStatus {
  if (!raw) return progressPercent > 0 ? 'Active' : 'Planning';
  const normalized = raw.trim().toLowerCase().replace(/_/g, ' ');
  const aliases: Record<string, InitiativeStatus> = {
    active: 'Active',
    approved: 'Approved',
    planning: 'Planning',
    'at risk': 'At Risk',
    'on hold': 'On Hold',
    complete: 'Complete',
    completed: 'Complete',
    'value tracking': 'Value Tracking',
    'approval pending': 'Approval Pending',
    'activation required': 'Activation Required',
  };
  if (aliases[normalized]) return aliases[normalized];
  const allowed: InitiativeStatus[] = [
    'Approval Pending', 'Approved', 'Activation Required', 'Planning',
    'Active', 'At Risk', 'On Hold', 'Complete', 'Value Tracking',
  ];
  const match = allowed.find(s => s.toLowerCase() === normalized);
  return match ?? 'Planning';
}

function mapMilestoneStatus(raw: string): MilestoneStatus {
  const map: Record<string, MilestoneStatus> = {
    'not started': 'Not Started',
    'in progress': 'In Progress',
    'at risk': 'At Risk',
    complete: 'Complete',
    completed: 'Complete',
    delayed: 'Delayed',
  };
  return map[raw.toLowerCase()] ?? 'Not Started';
}

function mapWorkstreamStatus(raw: string): WorkstreamStatus {
  const map: Record<string, WorkstreamStatus> = {
    'not started': 'Not Started',
    'in progress': 'In Progress',
    complete: 'Complete',
    completed: 'Complete',
    blocked: 'Blocked',
  };
  return map[raw.toLowerCase()] ?? 'Not Started';
}

function mapRiskSeverity(raw: string): RiskSeverity {
  const lower = raw.toLowerCase();
  if (lower === 'high' || lower === 'critical') return 'High';
  if (lower === 'medium' || lower === 'moderate') return 'Moderate';
  return 'Low';
}

function mapLikelihood(raw: string): 'Low' | 'Medium' | 'High' {
  const lower = raw.toLowerCase();
  if (lower === 'high') return 'High';
  if (lower === 'medium') return 'Medium';
  return 'Low';
}

function mapRiskStatus(raw: string): 'Open' | 'Mitigated' | 'Resolved' {
  const lower = raw.toLowerCase();
  if (lower === 'resolved') return 'Resolved';
  if (lower === 'mitigated') return 'Mitigated';
  return 'Open';
}

function mapSummaryMetric(m: CommandCenterSummaryMetric): MappedSummaryMetric {
  let value = coerceDisplayText(m.value_display ?? m.value);
  let sub = coerceDisplayText(m.description, '');

  if (m.id === 'milestones') {
    const complete = m.complete ?? 0;
    const total = m.total ?? 0;
    value = total > 0 ? `${complete}/${total}` : '—';
    sub = `${complete} of ${total} milestones complete`;
  } else if (m.id === 'next_milestone') {
    const raw = m.value_display ?? m.value;
    if (raw != null && typeof raw === 'object') {
      value = coerceDisplayText((raw as Record<string, unknown>).label ?? (raw as Record<string, unknown>).title);
      const due = coerceDisplayText(
        (raw as Record<string, unknown>).due_date_display
          ?? (raw as Record<string, unknown>).due_date,
        '',
      );
      sub = due && due !== '—' ? `Due ${due}` : sub || 'Upcoming milestone';
    } else if (!raw) {
      value = '—';
      sub = sub || 'No upcoming milestone';
    }
  }

  return {
    id: m.id,
    label: m.label,
    value,
    sub,
    progressPercent: m.progress_percent,
  };
}

function mapRisk(r: CommandCenterRisk): MappedRisk {
  return {
    id: r.risk_id,
    title: r.title || r.name,
    description: r.description,
    severity: mapRiskSeverity(r.severity || r.severity_display),
    likelihood: mapLikelihood(r.likelihood),
    owner: orFallback(r.owner, 'Unassigned'),
    mitigation: r.mitigation_plan || r.mitigation,
    status: mapRiskStatus(r.status),
  };
}

function mapResourceItem(item: CommandCenterResourceItem): MappedResourceItem {
  return {
    id: item.id,
    label: item.title,
    desc: item.description,
    action: item.action,
    available: item.available,
    path: item.path ?? item.alias_path,
    content: item.content,
    targetTab: item.target,
  };
}

function buildOverview(response: CommandCenterResponse): MappedOverview {
  const { tabs } = response;
  const overview = tabs.overview;
  const paths = tabs.resources?.paths ?? {};

  const decisionReferences = (tabs.resources?.decision_references ?? []).map(ref => ({
    id: ref.id,
    label: ref.title,
    path: ref.path ?? paths[ref.id as keyof typeof paths],
  }));

  if (!overview) {
    return {
      nextAction: null,
      objective: null,
      strategicValueDriver: null,
      whyApproved: null,
      expectedOutcomes: [],
      topPriorities: [],
      topRisks: [],
      decisionReferences,
    };
  }

  const overviewRecord = overview as CommandCenterOverviewTab & Record<string, unknown>;

  const topRisks: MappedOverviewRisk[] = (overview.top_risks ?? []).map(r => ({
    id: r.risk_id,
    title: r.title || r.name,
    severity: mapRiskSeverity(r.severity || r.severity_display),
    severityDisplay: orFallback(r.severity_display, r.severity),
  }));

  return {
    nextAction: optionalText(overview.recommended_next_action),
    objective: optionalText(overview.uc_description),
    strategicValueDriver: (() => {
      const raw = overview.primary_value_driver;
      if (raw == null || String(raw).trim() === '') return null;
      return normalizePrimaryValueDriverText(String(raw));
    })(),
    whyApproved: optionalText(overview.why_initiative_approved ?? overview.execution_rationale),
    expectedOutcomes: normalizeOverviewList(
      (overview.expected_outcomes ?? overviewRecord.expectedOutcomes) as unknown[],
    ),
    topPriorities: normalizeOverviewList(
      (overview.top_priorities ?? overviewRecord.topPriorities) as unknown[],
    ),
    topRisks,
    decisionReferences,
  };
}

export function mapCommandCenter(response: CommandCenterResponse): CommandCenterViewModel {
  const { header, sidebar, tabs } = response;
  const sidebarMeta = sidebar.initiative_metadata ?? response.initiative_metadata;
  const progressMetric = header.summary_metrics.find(m => m.id === 'overall_progress');
  const progressPercent = progressMetric?.progress_percent ?? 0;

  const status = mapInitiativeStatus(
    header.status_display
      ?? header.status
      ?? sidebar.initiative_status.status_display
      ?? sidebar.initiative_status.status,
    progressPercent,
  );

  const milestones = (tabs.milestones?.milestones ?? []).map(m => ({
    id: m.milestone_id,
    title: m.title || m.label,
    phase: m.phase,
    owner: orFallback(m.owner_display ?? m.owner ?? m.owner_role, 'Unassigned'),
    dueDate: orFallback(m.due_date ?? m.target_date ?? m.target, 'TBD'),
    status: mapMilestoneStatus(m.status),
    completionPct: m.completion_pct ?? m.progress_percent ?? 0,
    blockers: m.blocker ?? m.dependency_warning ?? undefined,
    valueNote: m.value_note ?? undefined,
  }));

  const risks = (tabs.risks?.risks ?? []).map(mapRisk);

  const financialKpis = tabs.metrics?.financial_kpis as {
    roi_narrative?: string;
    implementation_cost?: number | string | null;
    implementation_cost_display?: string | null;
  } | null;

  const implementationCostRaw = (() => {
    const raw = financialKpis?.implementation_cost;
    if (raw == null || raw === '') return null;
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n : null;
  })();

  const investmentDisplay = (() => {
    const display = (financialKpis?.implementation_cost_display ?? '').trim();
    if (implementationCostRaw != null) {
      return display || FALLBACK;
    }
    if (!display || display === '—' || display === '-' || display === '$0' || display === '0') {
      return FALLBACK;
    }
    return display;
  })();

  const headerPayback = orFallback(
    header.payback_display,
    header.payback_months != null ? `${header.payback_months} months` : FALLBACK,
  );

  const mapKpiRow = (k: {
    label: string;
    baseline_value: string | null;
    current_value: string | null;
    current_value_display?: string | null;
    target_value: string | null;
  }) => {
    const isPayback = /payback/i.test(k.label);
    // DMD-1217 / DMD-1202: Payback target must match ROI-model header payback.
    const target =
      isPayback && headerPayback !== FALLBACK
        ? headerPayback
        : formatMetricDisplayValue(orFallback(k.target_value, '—'));
    return {
      label: k.label,
      baseline: formatMetricDisplayValue(orFallback(k.baseline_value, '—')),
      current: formatMetricDisplayValue(
        orFallback(k.current_value_display ?? k.current_value, 'Not yet measured'),
      ),
      target,
    };
  };

  // Build team first so Sponsor / Owner on the portfolio + command-center header
  // can fall back to Team roles when initiative_metadata fields are empty.
  const team = (tabs.team?.team ?? []).map(member => ({
    id: member.team_member_id ? String(member.team_member_id) : undefined,
    role: orFallback(member.role ?? member.owner_role, 'Team Member'),
    name: orFallback(member.name ?? member.owner_display, 'TBD'),
    title: orFallback(member.title ?? member.owner_display, ''),
  }));

  const owner = resolvePersonName(
    header.initiative_owner ?? sidebarMeta?.initiative_owner,
    team,
    'Initiative Owner',
  );
  const sponsor = resolvePersonName(
    header.executive_sponsor ?? sidebarMeta?.executive_sponsor,
    team,
    'Executive Sponsor',
  );

  return {
    useCaseId: String(response.use_case_id),
    clientId: response.client_id,
    header: {
      title: header.title || header.name,
      breadcrumb: orFallback(header.breadcrumb, header.category_name),
      categoryName: header.category_name,
      status,
      owner,
      sponsor,
      timeline: orFallback(header.target_timeline_display, 'TBD'),
      payback: headerPayback,
      roiRange: orFallback(header.base_roi_range_display, FALLBACK),
      investment: investmentDisplay,
      confidence: orFallback(header.confidence_level, FALLBACK),
      duration: orFallback(header.initiative_duration_display, header.initiative_duration_months != null ? `${header.initiative_duration_months} months` : FALLBACK),
      phase: orFallback(
        formatExecutionPostureLabel(
          header.initiative_phase ?? sidebarMeta?.initiative_phase ?? tabs.overview?.initiative_phase,
        ),
        'Planning',
      ),
      summaryMetrics: header.summary_metrics.map(mapSummaryMetric),
    },
    overview: buildOverview(response),
    activationChecklist: (tabs.plans?.activation_checklist ?? []).map(item => ({
      index: item.index,
      label: coerceDisplayText(item.label, 'Checklist item'),
      done: item.done,
      status: coerceDisplayText(item.status, 'Pending'),
    })),
    checklistSummary: {
      total: tabs.plans?.activation_checklist_summary.total ?? 0,
      completed: tabs.plans?.activation_checklist_summary.completed ?? 0,
      progressPercent: tabs.plans?.activation_checklist_summary.progress_percent ?? 0,
    },
    workstreams: (tabs.plans?.workstreams ?? []).map(ws => ({
      id: ws.workstream_id,
      name: ws.name,
      // Prefer assigned owner / soft display; never force owner_role into the
      // editable field once the API reports an explicit empty owner (DMD-1209).
      owner: orFallback(ws.owner_display ?? ws.owner, ''),
      status: mapWorkstreamStatus(ws.status),
      targetDate: orFallback(ws.target ?? ws.target_date ?? ws.due_date, 'TBD'),
      notes: orFallback(ws.notes ?? ws.pipeline_notes ?? ws.description, ''),
    })),
    milestones,
    milestoneSummary: {
      total: tabs.milestones?.milestone_summary.total ?? milestones.length,
      complete: tabs.milestones?.milestone_summary.complete ?? 0,
      inProgress: tabs.milestones?.milestone_summary.in_progress ?? 0,
      notStarted: tabs.milestones?.milestone_summary.not_started ?? 0,
      delayed: tabs.milestones?.milestone_summary.delayed ?? 0,
    },
    milestoneExportPath: tabs.milestones?.export_path,
    risks,
    riskSummary: {
      high: tabs.risks?.risk_summary.high_severity ?? 0,
      moderate: tabs.risks?.risk_summary.moderate ?? 0,
      resolved: tabs.risks?.risk_summary.resolved ?? 0,
      open: tabs.risks?.risk_summary.open ?? 0,
    },
    team,
    approvalSignoff: (() => {
      const raw = response.approval_signoff;
      const completedCount = Math.max(0, Math.min(5, raw?.completed_count ?? 0));
      const steps = (raw?.steps ?? []).map((step) => ({
        id: step.id,
        label: step.label,
        cfo: Boolean(step.cfo),
        status: (step.status === 'done' || step.status === 'current' || step.status === 'pending'
          ? step.status
          : 'pending') as 'done' | 'current' | 'pending',
        source: step.source ?? null,
      }));
      return {
        completedCount,
        currentStepId: raw?.current_step_id ?? null,
        allComplete: Boolean(raw?.all_complete) || completedCount >= 5,
        steps,
      };
    })(),
    metrics: {
      banner: tabs.metrics?.value_realization_banner ?? null,
      financial: (tabs.metrics?.kpis ?? [])
        .filter(k => k.category === 'financial')
        .map(mapKpiRow),
      operational: (tabs.metrics?.kpis ?? [])
        .filter(k => k.category === 'operational')
        .map(mapKpiRow),
      clinical: (tabs.metrics?.kpis ?? [])
        .filter(k => k.category === 'clinical')
        .map(mapKpiRow),
      delivery: (tabs.metrics?.delivery_metrics ?? []).map(d => ({
        label: d.label,
        baseline: formatMetricDisplayValue(orFallback(d.baseline_value, '—')),
        current: formatMetricDisplayValue(
          orFallback(d.current_value_display ?? d.current_value_pct ?? d.current_value, '—'),
        ),
        target: formatMetricDisplayValue(
          orFallback(d.target_value_detail ?? d.target_value_pct ?? d.target_value, '—'),
        ),
      })),
      roiNarrative: financialKpis?.roi_narrative ?? profileRoiNarrative(tabs.resources?.profile_sections?.roi_narrative),
      implementationCost: implementationCostRaw,
    },
    resources: {
      approvalMaterials: (tabs.resources?.approval_materials ?? []).map(mapResourceItem),
      decisionReferences: (tabs.resources?.decision_references ?? []).map(mapResourceItem),
      executionMaterials: (tabs.resources?.execution_materials ?? []).map(mapResourceItem),
    },
    sidebar: {
      status,
      owner,
      sponsor,
      phase: orFallback(
        formatExecutionPostureLabel(
          header.initiative_phase ?? sidebarMeta?.initiative_phase ?? tabs.overview?.initiative_phase,
        ),
        'Planning',
      ),
      nextMilestone: sidebar.next_milestone
        ? {
            title: sidebar.next_milestone.title || sidebar.next_milestone.label,
            dueDate: orFallback(sidebar.next_milestone.due_date_display ?? sidebar.next_milestone.due_date, 'TBD'),
            completionPct: sidebar.next_milestone.completion_pct ?? 0,
            progressDisplay: sidebar.next_milestone.progress_display,
          }
        : null,
      topRisk: sidebar.top_risk
        ? {
            title: sidebar.top_risk.title || sidebar.top_risk.name,
            description: sidebar.top_risk.description,
            severity: mapRiskSeverity(sidebar.top_risk.severity),
            severityDisplay: orFallback(sidebar.top_risk.severity_display, sidebar.top_risk.severity),
          }
        : null,
      quickActions: sidebar.quick_actions.map(a => ({
        id: a.id,
        label: a.label,
        action: a.action,
        target: normalizeTabTarget(a.target),
        path: a.path,
        resourceId: a.resource_id,
      })),
    },
  };
}

function profileRoiNarrative(value: string | null | undefined): string | null {
  return value ?? null;
}

export function resourceContentPreview(content: Record<string, unknown> | undefined): string {
  if (!content) return 'No preview content available.';
  const keys = [
    'executive_summary',
    'roi_narrative',
    'rationale',
    'strategic_narrative',
    'recommendation',
    'change_management',
    'stakeholder_alignment',
    'risk_analysis',
  ];
  for (const key of keys) {
    const val = content[key];
    if (typeof val === 'string' && val.trim()) return val;
  }
  return JSON.stringify(content, null, 2);
}
